import api, { unwrap, downloadFile } from './client';

/**
 * Télécharge l'export Excel des 3 rapports People (Evolution, Year-end, People cost)
 * + un onglet Sources. Pattern aligné sur exportTenant() dans api/dataExchange.js.
 */
export async function exportPeopleReports(tenantId) {
  const today = new Date().toISOString().slice(0, 10);
  await downloadFile(
    `/hr/people-reports/${tenantId}/export`,
    `palestre_people_reports_${today}.xlsx`,
  );
}

/**
 * Télécharge le deck PowerPoint des 3 rapports People (8 slides).
 */
export async function exportPeopleReportsPptx(tenantId) {
  const today = new Date().toISOString().slice(0, 10);
  await downloadFile(
    `/hr/people-reports/${tenantId}/pptx`,
    `palestre_people_reports_${today}.pptx`,
  );
}

// Read-only endpoints during transition.

export const listHrPeople = (tid) =>
  unwrap(api.get(`/hr/people/${tid}`));

export const createHrPerson = (tid, payload) =>
  unwrap(api.post(`/hr/people/${tid}`, payload));

export const onboardEmployee = (tid, payload) =>
  unwrap(api.post(`/hr/people/${tid}/onboard`, payload));

export const updateHrPerson = (tid, pid, payload) =>
  unwrap(api.patch(`/hr/people/${tid}/${pid}`, payload));

export const deleteHrPerson = (tid, pid) =>
  unwrap(api.delete(`/hr/people/${tid}/${pid}`));

export const bulkUpdateHrPeople = (tid, payload) =>
  unwrap(api.post(`/hr/people/${tid}/bulk-update`, payload));

export const createHrContract = (tid, payload) =>
  unwrap(api.post(`/hr/contracts/${tid}`, payload));

export const updateHrContract = (tid, cid, payload) =>
  unwrap(api.patch(`/hr/contracts/${tid}/${cid}`, payload));

export const deleteHrContract = (tid, cid) =>
  unwrap(api.delete(`/hr/contracts/${tid}/${cid}`));

export const updateHrLppRate = (tid, lid, payload) =>
  unwrap(api.patch(`/hr/lpp-rates/${tid}/${lid}`, payload));

export const listHrContracts = (tid) =>
  unwrap(api.get(`/hr/contracts/${tid}`));

export const listHrAnnualExtras = (tid, year) => {
  const q = year != null ? `?year=${year}` : '';
  return unwrap(api.get(`/hr/annual-extras/${tid}${q}`));
};

export const updateHrAnnualExtras = (tid, eid, payload) =>
  unwrap(api.patch(`/hr/annual-extras/${tid}/${eid}`, payload));

export const bulkUpdateHrAnnualExtras = (tid, payload) =>
  unwrap(api.post(`/hr/annual-extras/${tid}/bulk-update`, payload));

export const copyHrAnnualExtrasYear = (tid, fromYear, toYear, overwrite = false) =>
  unwrap(api.post(`/hr/annual-extras/${tid}/copy-year`, {
    from_year: fromYear, to_year: toYear, overwrite,
  }));

// Compléments soumis aux charges sociales (hr_extra_items) — une ligne = un complément.
export const listHrExtraItems = (tid, year) => {
  const q = year != null ? `?year=${year}` : '';
  return unwrap(api.get(`/hr/extra-items/${tid}${q}`));
};

export const createHrExtraItem = (tid, payload) =>
  unwrap(api.post(`/hr/extra-items/${tid}`, payload));

export const updateHrExtraItem = (tid, itemId, payload) =>
  unwrap(api.patch(`/hr/extra-items/${tid}/${itemId}`, payload));

export const deleteHrExtraItem = (tid, itemId) =>
  unwrap(api.delete(`/hr/extra-items/${tid}/${itemId}`));

// Indemnités reçues (hr_received_indemnity_items) — une ligne = une indemnité reçue.
export const listHrReceivedIndemnityItems = (tid, year) => {
  const q = year != null ? `?year=${year}` : '';
  return unwrap(api.get(`/hr/received-indemnity-items/${tid}${q}`));
};

export const createHrReceivedIndemnityItem = (tid, payload) =>
  unwrap(api.post(`/hr/received-indemnity-items/${tid}`, payload));

export const updateHrReceivedIndemnityItem = (tid, itemId, payload) =>
  unwrap(api.patch(`/hr/received-indemnity-items/${tid}/${itemId}`, payload));

export const deleteHrReceivedIndemnityItem = (tid, itemId) =>
  unwrap(api.delete(`/hr/received-indemnity-items/${tid}/${itemId}`));

// Frais (hr_expense_items) — une ligne = un frais.
export const listHrExpenseItems = (tid, year) => {
  const q = year != null ? `?year=${year}` : '';
  return unwrap(api.get(`/hr/expense-items/${tid}${q}`));
};

export const createHrExpenseItem = (tid, payload) =>
  unwrap(api.post(`/hr/expense-items/${tid}`, payload));

export const updateHrExpenseItem = (tid, itemId, payload) =>
  unwrap(api.patch(`/hr/expense-items/${tid}/${itemId}`, payload));

export const deleteHrExpenseItem = (tid, itemId) =>
  unwrap(api.delete(`/hr/expense-items/${tid}/${itemId}`));

export const listHrLppRates = (tid) =>
  unwrap(api.get(`/hr/lpp-rates/${tid}`));

export const syncLppRatesFromPeople = (tid) =>
  unwrap(api.post(`/hr/lpp-rates/${tid}/sync-from-people`));

// ── LPP par année (onglet dédié, taux source de vérité + report + défaut) ───
export const listLppForYear = (tid, year, filter = 'active') =>
  unwrap(api.get(`/hr/lpp/${tid}/${year}?filter=${filter}`));

// payload : { person_id, year, mode: 'pct'|'chf',
//             lpp_empr_pct, lpp_empl_pct, lpp_empr_chf_monthly, lpp_empl_chf_monthly }
export const upsertLpp = (tid, payload) =>
  unwrap(api.put(`/hr/lpp/${tid}`, payload));

// payload : { year, lpp_empr_pct, lpp_empl_pct, filter: 'active'|'all' }
export const massEditLpp = (tid, payload) =>
  unwrap(api.post(`/hr/lpp/${tid}/mass-edit`, payload));

// Prix standard (prix de vente horaire hypothétique) par personne / année.
export const listStandardPriceForYear = (tid, year, filter = 'active') =>
  unwrap(api.get(`/hr/standard-price/${tid}/${year}?filter=${filter}`));
// payload : { person_id, year, hourly_price }  (hourly_price null = efface)
export const upsertStandardPrice = (tid, payload) =>
  unwrap(api.put(`/hr/standard-price/${tid}`, payload));

export const peopleCostSynthesis = (tid, year, filter = 'all') =>
  unwrap(api.get(`/hr/people-synthesis/${tid}?year=${year}&filter=${filter}`));

// Photo « à date » du dernier mois réel clos : coûts YTD + effectif/EPT.
export const peopleCostSynthesisYtd = (tid, year, filter = 'all') =>
  unwrap(api.get(`/hr/people-synthesis-ytd/${tid}?year=${year}&filter=${filter}`));

// ── Payroll simulation movements (DB-backed) ────────────────────────────────
export const listPayrollMovements = (tid) =>
  unwrap(api.get(`/payroll-simulation/${tid}/movements`));

export const createPayrollMovement = (tid, payload) =>
  unwrap(api.post(`/payroll-simulation/${tid}/movements`, payload));

export const updatePayrollMovement = (tid, mid, payload) =>
  unwrap(api.patch(`/payroll-simulation/${tid}/movements/${mid}`, payload));

export const deletePayrollMovement = (tid, mid) =>
  unwrap(api.delete(`/payroll-simulation/${tid}/movements/${mid}`));

export const deleteAllPayrollMovements = (tid) =>
  unwrap(api.delete(`/payroll-simulation/${tid}/movements`));

// ── People Evolution automatic reading ─────────────────────────────────────
export const getPeopleEvolutionReading = (tid, year) =>
  unwrap(api.get(`/people-evolution/${tid}/${year}/reading`));

/**
 * Mois de versement du 13e/14e salaire (People › Données › Autres paramètres).
 * Route sous /hr → périmètre `people` : un RH peut l'écrire, sans avoir accès
 * aux réglages du client (devise, TVA), réservés aux rôles de SETTINGS_ROLES.
 */
export function updateSalaryMonths(tenantId, fields) {
  return unwrap(api.patch(`/hr/${tenantId}/salary-months`, fields));
}

// ── Salaire de référence MANUEL par département (Trajectoire) ──────────────
// Comble une lacune seulement : le dérivé (présents douze mois) prime.
export const listDeptRefSalaries = (tid) =>
  unwrap(api.get(`/hr/dept-ref-salaries/${tid}`));
export const upsertDeptRefSalary = (tid, department, annualSalary) =>
  unwrap(api.put(`/hr/dept-ref-salaries/${tid}`, { department, annual_salary: annualSalary }));
